import { jsPDF } from 'jspdf';

// Builds a polished, branded 1-page investor/bank snapshot PDF from the live
// /api/founder/finance payload. Uses only jsPDF primitives (no html2canvas) for
// crisp vector text. Green = brand, gold = valuation, red = burn.
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const money2 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const FOREST = [26, 67, 49], DEEP = [15, 45, 31], GOLD = [201, 168, 91], LIME = [180, 211, 48];
const CREAM = [250, 246, 236], INK = [45, 36, 30], MUTED = [92, 102, 92], LEAF = [78, 122, 51], TERRA = [198, 107, 74];

export function exportInvestorOnePager(finance, multKey = 'standard') {
  const val = finance?.valuation || {};
  const gross = finance?.gross || {};
  const net = finance?.net || {};
  const readiness = finance?.readiness || {};
  const valuation = val.values?.[multKey] ?? 0;
  const mult = val.multipliers?.[multKey] ?? 7;
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' });

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = 595, M = 40;

  // ---- header band ----
  doc.setFillColor(...DEEP); doc.rect(0, 0, W, 104, 'F');
  doc.setFillColor(...LIME); doc.rect(0, 104, W, 4, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(24); doc.setTextColor(...CREAM);
  doc.text('Homegrown', M, 52);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...LIME);
  doc.text('CONFIDENTIAL INVESTOR SNAPSHOT', M, 72);
  doc.setTextColor(180, 190, 180); doc.setFontSize(9);
  doc.text('Premium AI vegetable-growing app · NZ / AU', M, 88);
  doc.setFontSize(9); doc.setTextColor(200, 205, 200);
  doc.text(dateStr, W - M, 52, { align: 'right' });
  doc.text('nzd', W - M, 66, { align: 'right' });

  // ---- valuation hero ----
  let y = 150;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...MUTED);
  doc.text('ESTIMATED COMPANY VALUATION', M, y);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(40); doc.setTextColor(...GOLD);
  doc.text(money0(valuation), M, y + 40);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(...MUTED);
  doc.text(`${money0(val.arr)} ARR × ${mult}× SaaS multiple  ·  ${(val.mom_growth_pct || 0) >= 0 ? '+' : ''}${val.mom_growth_pct || 0}% MoM`, M, y + 62);

  // ---- key metrics grid (3 x 2) ----
  y = 236;
  const metrics = [
    { v: money2(gross.mrr), l: 'Monthly recurring revenue', c: FOREST },
    { v: money0(gross.arr), l: 'Annual run-rate (ARR)', c: FOREST },
    { v: `${finance?.active_users || 0}`, l: 'Active subscribers', c: FOREST },
    { v: `${net.net_profit >= 0 ? '' : '-'}${money0(Math.abs(net.net_profit))}`, l: 'Net profit / month', c: net.net_profit >= 0 ? LEAF : TERRA },
    { v: `${net.margin_pct || 0}%`, l: 'Net margin', c: net.net_profit >= 0 ? LEAF : TERRA },
    { v: `${(finance?.monthly_growth_pct || 0) >= 0 ? '+' : ''}${finance?.monthly_growth_pct || 0}%`, l: 'MoM MRR growth', c: LEAF },
  ];
  const cols = 3, gap = 12, cw = (W - M * 2 - gap * (cols - 1)) / cols, ch = 74;
  metrics.forEach((m, i) => {
    const cx = M + (i % cols) * (cw + gap);
    const cy = y + Math.floor(i / cols) * (ch + gap);
    doc.setFillColor(...CREAM); doc.setDrawColor(225, 225, 218); doc.roundedRect(cx, cy, cw, ch, 8, 8, 'FD');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(19); doc.setTextColor(...m.c);
    doc.text(String(m.v), cx + 14, cy + 34);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
    doc.text(m.l, cx + 14, cy + 54);
  });

  // ---- valuation scenarios ----
  y = 236 + (ch + gap) * 2 + 24;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...FOREST);
  doc.text('Valuation scenarios (ARR multiple)', M, y);
  const scen = [
    { k: 'conservative', label: 'Conservative' },
    { k: 'standard', label: 'Standard' },
    { k: 'aggressive', label: 'Aggressive' },
  ];
  y += 12;
  const sw = (W - M * 2 - gap * 2) / 3;
  scen.forEach((sc, i) => {
    const cx = M + i * (sw + gap);
    const active = sc.k === multKey;
    if (active) { doc.setFillColor(...FOREST); } else { doc.setFillColor(...CREAM); }
    doc.setDrawColor(225, 225, 218); doc.roundedRect(cx, y, sw, 58, 8, 8, 'FD');
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.setTextColor(...(active ? LIME : MUTED));
    doc.text(`${sc.label} · ${val.multipliers?.[sc.k] ?? ''}×`, cx + 12, y + 22);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(17);
    doc.setTextColor(...(active ? CREAM : GOLD));
    doc.text(money0(val.values?.[sc.k]), cx + 12, y + 44);
  });

  // ---- investor readiness ----
  y += 84;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...FOREST);
  doc.text(`Investor readiness  ·  ${readiness.overall_score || 0}/100`, M, y);
  y += 14;
  (readiness.factors || []).forEach((f) => {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5); doc.setTextColor(...INK);
    doc.text(`${f.label} — ${f.value}`, M, y + 10);
    doc.setTextColor(...MUTED); doc.text(`${f.score}`, W - M, y + 10, { align: 'right' });
    // bar
    const bx = M, by = y + 16, bw = W - M * 2;
    doc.setFillColor(225, 225, 218); doc.roundedRect(bx, by, bw, 6, 3, 3, 'F');
    doc.setFillColor(...LEAF); doc.roundedRect(bx, by, Math.max(bw * (f.score / 100), 4), 6, 3, 3, 'F');
    y += 34;
  });

  // ---- footer ----
  doc.setDrawColor(225, 225, 218); doc.line(M, 792, W - M, 792);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(...MUTED);
  doc.text('Revenue, MRR, ARR & valuation are live figures. Platform costs and product COGS are modeled estimates.', M, 806);
  doc.text('Strictly private & confidential — prepared by the Homegrown Founder Suite.', M, 818);
  doc.setTextColor(150, 155, 150);
  doc.text(`Generated ${now.toLocaleString('en-NZ')}`, W - M, 818, { align: 'right' });

  doc.save(`Homegrown-Investor-Snapshot-${now.toISOString().slice(0, 10)}.pdf`);
}
